/**
 * Form aturan alert (ADR-0055 Fase 3 slice 4) — konstanta & validasi MURNI, teruji di Node.
 *
 * Allowlist metrik & operator & severity SENGAJA mencerminkan `alert.dto.ts` server
 * (ALERT_METRIC_KEYS = TELEMETRY_METRIC_KEYS tanpa 'status'; ALERT_OPERATORS; ALERT_SEVERITIES).
 * Bila server berubah, keduanya harus bergerak bersama — uji di sini menjaga agar form tak
 * menawarkan pilihan yang pasti ditolak server (400).
 */
import type { AlertOperator, AlertSeverity, CreateAlertRuleBody } from '../api/types';

/** = TELEMETRY_METRIC_KEYS tanpa 'status' (teks, tak punya ambang). Cermin alert.dto.ts. */
export const METRIK_ALERT = [
  'cpu_percent',
  'memory_percent',
  'disk_percent',
  'temperature_c',
  'uptime_seconds',
  'error_code',
] as const;

export const LABEL_METRIK: Record<string, string> = {
  cpu_percent: 'CPU (%)',
  memory_percent: 'Memori (%)',
  disk_percent: 'Disk (%)',
  temperature_c: 'Suhu (°C)',
  uptime_seconds: 'Uptime (detik)',
  error_code: 'Kode error',
};

export const OPERATOR_ALERT: readonly AlertOperator[] = ['gt', 'gte', 'lt', 'lte', 'eq', 'ne'];
export const LABEL_OPERATOR: Record<AlertOperator, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  ne: '≠',
};

export const SEVERITY_ALERT: readonly AlertSeverity[] = ['warning', 'critical', 'info'];
export const LABEL_SEVERITY: Record<AlertSeverity, string> = {
  info: 'Info',
  warning: 'Perhatian',
  critical: 'Kritis',
};

/** Keadaan form yang disunting pengguna sebelum dikirim. */
export interface FormAturan {
  readonly metricKey: string;
  readonly operator: AlertOperator;
  readonly threshold: string; // mentah dari input; divalidasi jadi angka
  readonly severity: AlertSeverity;
  readonly autoCreateTask: boolean;
  readonly taskTypeCode: string;
}

export const FORM_ATURAN_AWAL: FormAturan = {
  metricKey: 'cpu_percent',
  operator: 'gt',
  threshold: '',
  severity: 'warning',
  autoCreateTask: false,
  taskTypeCode: '',
};

/**
 * Masalah pertama pada form, atau null bila sah. Pesan dalam Bahasa Indonesia, siap tampil.
 * Mencerminkan aturan server: metrik ∈ allowlist, operator & severity valid, threshold angka
 * berhingga, dan taskTypeCode WAJIB bila autoCreateTask (service menolak jika tidak — ALERT-002).
 */
export function masalahFormAturan(form: FormAturan): string | null {
  if (!(METRIK_ALERT as readonly string[]).includes(form.metricKey)) {
    return 'Pilih metrik yang valid.';
  }
  if (!OPERATOR_ALERT.includes(form.operator)) return 'Pilih operator yang valid.';
  if (!SEVERITY_ALERT.includes(form.severity)) return 'Pilih tingkat keparahan yang valid.';
  const angka = Number(form.threshold);
  if (form.threshold.trim() === '' || !Number.isFinite(angka)) {
    return 'Ambang harus berupa angka.';
  }
  if (form.autoCreateTask && form.taskTypeCode.trim() === '') {
    return 'Pilih jenis tugas untuk tiket otomatis.';
  }
  return null;
}

/** Ubah form yang sudah tervalidasi menjadi body permintaan. Panggil hanya jika masalah = null. */
export function bodyDariForm(form: FormAturan): CreateAlertRuleBody {
  const body: CreateAlertRuleBody = {
    metricKey: form.metricKey,
    operator: form.operator,
    threshold: Number(form.threshold),
    severity: form.severity,
    autoCreateTask: form.autoCreateTask,
  };
  return form.autoCreateTask ? { ...body, taskTypeCode: form.taskTypeCode.trim() } : body;
}
